import {
  errors,
  IOauthConsentRedirectDto,
  ITranslatableItem,
  Log,
  RoutePaths,
  SimpleResult,
} from "@ryde/shared";

/**
 * Whether a stored login returnTo belongs to the OAuth consent flow. Consent stores its own URL
 * before bouncing through login; once the flow ends (redirect back to the client, or a terminal
 * error) that entry must be cleared — a stale one would send a LATER login to a consumed/expired
 * request. A destination owned by an unrelated flow is never ours to delete (RYDE-7833).
 */
export const isOauthOwnedReturnTo = (returnTo: unknown): returnTo is string => {
  return (
    typeof returnTo === "string" &&
    returnTo.startsWith(RoutePaths.OAUTH_CONSENT)
  );
};

/** SimpleResult errors carry an i18n key — extract it to branch on specific backend outcomes. */
export const readErrorTranslationKey = (error: unknown): string | undefined => {
  return (error as ITranslatableItem | undefined)?.translationKey;
};

/**
 * What the consent screen should do next. Keeping the decision here (pure) makes the approve/deny
 * outcome — including transport failure — unit-testable without React (RYDE-7833).
 */
export type TConsentAction =
  | { kind: "redirect"; redirectTo: string }
  | { kind: "forceLogin" }
  | { kind: "error" };

/** Map a consent API result to the next action. */
export const resolveConsentResult = (
  result: SimpleResult<IOauthConsentRedirectDto> | undefined,
): TConsentAction => {
  if (result?.success && result.data?.redirectTo) {
    return { kind: "redirect", redirectTo: result.data.redirectTo };
  }

  // Strict tenant / prompt=login: session isn't fresh enough — bounce through a real login.
  if (
    readErrorTranslationKey(result?.error) === errors.oauth.freshLoginRequired
  ) {
    return { kind: "forceLogin" };
  }

  return { kind: "error" };
};

/**
 * Run an approve/deny call and map its outcome to an action. ANY transport failure (axios can
 * still throw after its retries) is caught and turned into the error action, so the caller never
 * faces an unhandled rejection and always stays inside the consent state machine (RYDE-7833).
 */
export const runConsentApiAction = async (
  apiCall: () => Promise<SimpleResult<IOauthConsentRedirectDto> | undefined>,
): Promise<TConsentAction> => {
  try {
    return resolveConsentResult(await apiCall());
  } catch (error) {
    Log.warn("OAuth consent action failed", error);

    return { kind: "error" };
  }
};

/**
 * What to do when approve answers freshLoginRequired. The first time, bounce through a forced
 * login. If a forced login was ALREADY attempted for this very request and consent still refuses,
 * the IdP could not prove a fresh credential entry (Microsoft without a verifiable auth_time, a
 * silent SSO return) — looping back to login would never end, so the flow stops with an
 * explanation instead (RYDE-7833).
 */
export type TForcedLoginStep = "login" | "stop";

export const resolveForcedLoginStep = ({
  attemptedRequestId,
  requestId,
}: {
  attemptedRequestId: string | null | undefined;
  requestId: string;
}): TForcedLoginStep => (attemptedRequestId === requestId ? "stop" : "login");
